<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePreOrderImage } from '~/composables/usePreOrderImage'

const props = defineProps({
  image: {
    type: Object,
    required: true,
  },
  soldOut: {
    type: Boolean,
    default: false,
  },
  productTitle: {
    type: String,
    default: '',
  },
  lazy: {
    default: true,
    type: Boolean,
  },
  sizes: {
    default: 'sm:540px md:920px lg:1200px',
    required: false,
  },
  preOrder: {
    type: Boolean,
    default: false,
  },
  badges: {
    type: Array,
    default: () => [],
  },
})
const { locale } = useI18n()
const { preOrderImage } = usePreOrderImage()

const soldOutImage = computed(() => {
  if (locale.value === 'fr') {
    return '/images/product/Label-Sold-Out-FR.svg'
  }
  return '/images/product/Label-Sold-Out-DE.svg'
})

const BADGE_POSITIONS = {
  LEFT_BOTTOM: 'left_bottom',
  RIGHT_TOP: 'right_top',
  RIGHT_BOTTOM: 'right_bottom',
}

const dynamicBadges = computed(() => props.badges || [])

const sortedBadges = computed(() => {
  const badges = Array.isArray(dynamicBadges.value)
    ? dynamicBadges.value
    : []

  return [...badges].sort(
    (first, second) => (first?.sort_order ?? 0) - (second?.sort_order ?? 0),
  )
})

function normalizePosition(position) {
  switch (position) {
    case BADGE_POSITIONS.RIGHT_TOP:
    case 'top_right':
      return BADGE_POSITIONS.RIGHT_TOP
    case BADGE_POSITIONS.RIGHT_BOTTOM:
    case 'right_bottom':
    case 'bottom_right':
      return BADGE_POSITIONS.RIGHT_BOTTOM
    case BADGE_POSITIONS.LEFT_BOTTOM:
    case 'bottom_left':
    default:
      return BADGE_POSITIONS.LEFT_BOTTOM
  }
}

function createRenderableBadge(badge) {
  if (!badge) {
    return null
  }

  const key = badge.code || badge.label || badge.name
  const alt = badge.image?.alt || badge.label || badge.name || 'badge'

  if (badge.image?.src) {
    return {
      key,
      src: badge.image.src,
      alt,
    }
  }

  return {
    key,
    alt,
    label: badge.label || badge.name || badge.code,
  }
}

function mapRenderableBadges(position) {
  return sortedBadges.value
    .filter(badge => normalizePosition(badge?.position) === position)
    .map((badge) => {
      const renderableBadge = createRenderableBadge(badge)

      if (!renderableBadge) {
        return null
      }

      return {
        ...renderableBadge,
        key: `dynamic-${badge.code || renderableBadge.key}`,
      }
    })
    .filter(Boolean)
}

const leftBottomBadges = computed(() =>
  mapRenderableBadges(BADGE_POSITIONS.LEFT_BOTTOM),
)

const rightTopBadges = computed(() =>
  mapRenderableBadges(BADGE_POSITIONS.RIGHT_TOP),
)

const rightBottomBadges = computed(() =>
  mapRenderableBadges(BADGE_POSITIONS.RIGHT_BOTTOM),
)
</script>

<template>
  <div class="product-image">
    <NuxtImg
      :sizes="sizes"
      densities="1x 2x"
      :loading="lazy ? 'lazy' : 'eager'"
      :src="
        image?.original || image?.src
          ? image?.original || image?.src
          : '#'
      "
      :alt="props.productTitle || image?.alt"
      format="webp"
      class="product-image__image"
      :placeholder="[300, 200, 60, 2]"
    />
    <div
      v-if="leftBottomBadges.length"
      class="product-image__labels"
    >
      <template v-for="badge in leftBottomBadges" :key="badge.key">
        <img
          v-if="badge.src"
          :src="badge.src"
          :alt="badge.alt"
          loading="lazy"
        >
        <span
          v-else
          class="product-image__badge-text"
        >{{ badge.label }}</span>
      </template>
    </div>
    <div
      v-if="rightTopBadges.length"
      class="product-image__right-top"
    >
      <template v-for="badge in rightTopBadges" :key="badge.key">
        <img
          v-if="badge.src"
          :src="badge.src"
          :alt="badge.alt"
          loading="lazy"
        >
        <span
          v-else
          class="product-image__badge-text"
        >{{ badge.label }}</span>
      </template>
    </div>
    <div
      v-if="rightBottomBadges.length"
      class="product-image__right-bottom"
    >
      <template v-for="badge in rightBottomBadges" :key="badge.key">
        <img
          v-if="badge.src"
          :src="badge.src"
          :alt="badge.alt"
          loading="lazy"
        >
        <span
          v-else
          class="product-image__badge-text"
        >{{ badge.label }}</span>
      </template>
    </div>
    <div
      v-if="soldOut || preOrder"
      class="d-flex align-items-center product-image__overlay"
      :class="{ 'product-image__sold-out': soldOut }"
    >
      <img
        v-if="soldOut"
        :src="soldOutImage"
        alt="sold out"
        loading="lazy"
        class="img-fluid"
      >
      <img
        v-else-if="preOrder"
        :src="preOrderImage"
        alt="sold out"
        loading="lazy"
        class="img-fluid"
      >
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/scss/variables.scss';
@import '@/node_modules/bootstrap/scss/mixins';

.product-image {
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 150px;
  position: relative;
  overflow: hidden;

  &__image {
    width: 100%;
    object-fit: cover;
    min-width: 100%;
    min-height: 100%;
  }

  &__labels {
    position: absolute;
    width: 100%;
    bottom: 0;
    left: 0;
    z-index: 10;
    padding: 10px;
    display: flex;
    align-items: center;
    gap: clamp(2px, 1.5%, 10px);
    height: 100px;
    max-height: 30%;

    img {
      height: auto;
      height: 40px;
      width: auto;
      max-width: 25%;
      @media (min-width: 768px) {
        max-height: 50px;
      }
    }
  }

  &__right-top {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 10;
    padding: 10px;
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    gap: clamp(2px, 1.5%, 10px);
    width: 100%;
    //height: clamp(100px, 10vw, 120px);
    max-height: 40%;

    img {
      height: auto;
      max-height: 100%;
      max-width: 22%;
    }
    @media (max-width: 576px) {
      height: 95px;
    }
  }

  &__right-bottom {
    position: absolute;
    bottom: 0;
    right: 0;
    z-index: 10;
    padding: 10px;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: flex-end;
    gap: clamp(2px, 1.5%, 10px);
    width: 100%;
    height: 100px;
    max-height: 40%;

    img {
      height: auto;
      max-height: 100%;
      width: auto;
    }
    @media (max-width: 576px) {
      height: 90px;
      img {
        height: 40px;
      }
    }
  }

  &__badge-text {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 6px 12px;
    border-radius: 999px;
    background-color: rgba(0, 0, 0, 0.75);
    color: $color-white;
    font-size: clamp(10px, 0.28vw + 9.2px, 13px);
    font-weight: 600;
    text-transform: uppercase;
    text-align: center;
    letter-spacing: 0.04em;
    line-height: 1;
    white-space: nowrap;
  }
  &__overlay {
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    right: 0;
    z-index: 20;
    img {
      display: flex;
      max-height: 253px;
      max-width: 395px;
      object-fit: contain;
      margin: auto;
      // safari fix
      height: 100%;
      width: 100%;

      @include media-breakpoint-down(640) {
        max-height: 100%;
        max-width: 100%;
      }
    }
  }
  &__sold-out {
    background-color: rgba(0, 0, 0, 0.8);
  }
}
</style>
